import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Alert, CircularProgress, Chip, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PublicIcon from '@mui/icons-material/Public';
import axios from 'axios';

const API = '/api/rrhh';

const TIPOS = [
  { value: 'NACIONAL', label: 'Feriado Nacional' },
  { value: 'REGIONAL', label: 'Feriado Regional' },
  { value: 'EMPRESA', label: 'Específico de empresa' },
];

export default function FeriadosRRHH() {
  const [feriados, setFeriados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [anioFiltro, setAnioFiltro] = useState(new Date().getFullYear());

  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState({ fecha: '', descripcion: '', tipo: 'NACIONAL' });
  const [saving, setSaving] = useState(false);

  const [eliminarId, setEliminarId] = useState(null);

  const anios = [new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1];

  const cargar = useCallback(() => {
    setLoading(true); setError('');
    axios.get(`${API}/feriados?anio=${anioFiltro}`)
      .then(r => setFeriados(r.data))
      .catch(e => setError(e.response?.data?.error || 'Error al cargar'))
      .finally(() => setLoading(false));
  }, [anioFiltro]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleCrear = async () => {
    if (!form.fecha || !form.descripcion) return;
    setSaving(true);
    try {
      await axios.post(`${API}/feriados`, form);
      setDlgOpen(false);
      setForm({ fecha: '', descripcion: '', tipo: 'NACIONAL' });
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al crear');
    } finally { setSaving(false); }
  };

  const handleEliminar = async () => {
    try {
      await axios.delete(`${API}/feriados/${eliminarId}`);
      setEliminarId(null);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al eliminar');
    }
  };

  const tipoColor = { NACIONAL: 'primary', REGIONAL: 'secondary', EMPRESA: 'warning' };

  const fechaLocal = (f) => {
    if (!f) return '';
    const d = new Date(f);
    return d.toLocaleDateString('es-PE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
  };

  const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const feriadosPorMes = meses.map((m, i) => ({
    mes: m,
    items: feriados.filter(f => {
      const d = new Date(f.fecha);
      return d.getUTCMonth() === i;
    }),
  })).filter(g => g.items.length > 0);

  return (
    <Box p={2}>
      <Typography variant="h5" fontWeight="bold" color="purple" gutterBottom>
        <CalendarMonthIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Feriados Nacionales y Laborales
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <TextField select label="Año" size="small" value={anioFiltro}
            onChange={e => setAnioFiltro(e.target.value)} sx={{ minWidth: '8em' }}>
            {anios.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: 'purple' }}
            onClick={() => setDlgOpen(true)}>
            Agregar Feriado
          </Button>
          <Typography variant="body2" color="text.secondary">
            <PublicIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            Los feriados nacionales aplican a todas las empresas automáticamente
          </Typography>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box p={3} textAlign="center"><CircularProgress color="secondary" /></Box>
      ) : (
        <Box display="flex" flexWrap="wrap" gap={2}>
          {feriadosPorMes.length === 0 ? (
            <Paper sx={{ p: 3, width: '100%', textAlign: 'center', color: '#888' }}>
              No hay feriados registrados para {anioFiltro}
            </Paper>
          ) : feriadosPorMes.map(grupo => (
            <Paper key={grupo.mes} sx={{ p: 0, minWidth: 280, flex: '1 1 280px' }}>
              <Box sx={{ bgcolor: 'purple', p: 1.5, borderRadius: '4px 4px 0 0' }}>
                <Typography variant="subtitle2" color="white" fontWeight="bold">{grupo.mes}</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {grupo.items.map(f => (
                      <TableRow key={f.id} hover>
                        <TableCell sx={{ fontSize: 11, width: 40, color: '#666', fontWeight: 'bold' }}>
                          {new Date(f.fecha).getUTCDate()}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{f.descripcion}</TableCell>
                        <TableCell sx={{ fontSize: 10, pr: 0.5 }}>
                          <Chip label={f.tipo || 'NACIONAL'} size="small"
                            color={tipoColor[f.tipo] || 'primary'} sx={{ fontSize: 9 }} />
                        </TableCell>
                        <TableCell sx={{ width: 32, p: 0.5 }}>
                          <IconButton size="small" color="error" onClick={() => setEliminarId(f.id)}>
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ))}
        </Box>
      )}

      {/* Dialog Nuevo Feriado */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'purple', color: 'white' }}>Agregar Feriado</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Fecha *" type="date" size="small" value={form.fecha}
            onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: '16em' }} />
          <TextField label="Descripción *" size="small" value={form.descripcion}
            onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
            sx={{ minWidth: '16em' }} />
          <TextField select label="Tipo" size="small" value={form.tipo}
            onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} sx={{ minWidth: '16em' }}>
            {TIPOS.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Cancelar</Button>
          <Button variant="contained" sx={{ bgcolor: 'purple' }} onClick={handleCrear} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm eliminar */}
      <Dialog open={!!eliminarId} onClose={() => setEliminarId(null)}>
        <DialogTitle>¿Eliminar feriado?</DialogTitle>
        <DialogContent>
          <Typography>Esta acción no se puede deshacer.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEliminarId(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleEliminar}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
